/**
 * src/lib/nav/reloadRestore.js — preserve the user's spot across an UPDATE-banner
 * reload (CEO 2026-06-18: "refresh on the same spot, ideally optimistically — I
 * always have to navigate back to the project to test").
 *
 * AppShell continuously saves the current {tab, projectId, dealId} to
 * sessionStorage. The UpdateBanner marks an intent flag right before
 * window.location.reload(). On the next boot AppShell calls consumeReloadRestore();
 * a non-null result restores the tab and re-opens the open detail — a project
 * (requestProjectOpen) OR a deal (requestDealOpen, TICKET-219). Scoped to an
 * EXPLICIT banner reload via the flag, so a fresh app open still lands on the
 * default view. sessionStorage (not local) so it never leaks across app launches.
 *
 * The detail's INNER sub-tab lives in transient component state, so the reload
 * (or any remount) would drop it back to Overview. saveDetailTab/readDetailTab
 * persist it per-entity so DealDetail/ProjectWorkspace can seed their initial tab.
 */
const NAV_KEY = 'acn_reload_nav';
const FLAG_KEY = 'acn_reload_restore';
const TAB_PREFIX = 'acn_detail_tab:';

function store() {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null; } catch { return null; }
}

/** Continuously called by AppShell as the active tab / open project / open deal change. */
export function saveCurrentNav(nav) {
  const s = store();
  if (!s) return;
  try {
    s.setItem(NAV_KEY, JSON.stringify({
      tab: nav?.tab || null,
      projectId: nav?.projectId || null,
      dealId: nav?.dealId || null,
    }));
  } catch { /* quota / private mode — restore is best-effort */ }
}

/** Called by UpdateBanner immediately before window.location.reload(). */
export function markReloadRestore() {
  const s = store();
  if (!s) return;
  try { s.setItem(FLAG_KEY, '1'); } catch { /* best-effort */ }
}

// TICKET-1439: iOS reloads the home-screen PWA while the camera / photo picker is
// open (prod trace 2026-10-26: a fresh 'initial' boot mid-capture, the user dumped
// off the deal). That reload is the OS's, not ours, so nothing marked it. Mark it
// ourselves before opening the picker. The OS reload can also drop sessionStorage,
// so the mark carries its own copy of the nav + detail tab in localStorage, with a
// TTL so a mark left behind by a cancelled picker never hijacks a later app open.
const PICKER_KEY = 'acn_picker_restore';
const PICKER_TTL_MS = 10 * 60 * 1000;

function local() {
  try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch { return null; }
}

/** Call right before opening a camera / file picker that may reload the page. */
export function markPickerOpen(now = Date.now()) {
  const s = store();
  const l = local();
  if (!s || !l) return;
  try {
    const nav = JSON.parse(s.getItem(NAV_KEY) || 'null');
    if (!nav || !(nav.tab || nav.projectId || nav.dealId)) return;
    const kind = nav.projectId ? 'project' : (nav.dealId ? 'deal' : null);
    const id = nav.projectId || nav.dealId;
    const detailTab = kind ? readDetailTab(kind, id) : null;
    l.setItem(PICKER_KEY, JSON.stringify({ at: now, nav, kind, id, detailTab }));
  } catch { /* best-effort */ }
}

/** Call when the picker returned without a reload (onChange ran in this page). */
export function clearPickerOpen() {
  try { local()?.removeItem(PICKER_KEY); } catch { /* best-effort */ }
}

function consumePickerRestore(now) {
  const l = local();
  if (!l) return null;
  try {
    const raw = l.getItem(PICKER_KEY);
    l.removeItem(PICKER_KEY);
    const m = raw ? JSON.parse(raw) : null;
    if (!m?.nav || !(now - m.at >= 0 && now - m.at <= PICKER_TTL_MS)) return null;
    if (m.kind && m.id && m.detailTab) saveDetailTab(m.kind, m.id, m.detailTab);
    return m.nav;
  } catch {
    return null;
  }
}

/**
 * Called once on AppShell boot. Returns { tab, projectId, dealId } to restore ONLY
 * when an explicit reload was marked (update banner, or a camera/picker that was
 * open when the page reloaded); otherwise null. One-shot — clears flag + nav.
 */
export function consumeReloadRestore(now = Date.now()) {
  const picker = consumePickerRestore(now);
  const s = store();
  if (!s) return picker;
  try {
    if (s.getItem(FLAG_KEY) !== '1') return picker;
    s.removeItem(FLAG_KEY);
    const raw = s.getItem(NAV_KEY);
    s.removeItem(NAV_KEY);
    if (!raw) return null;
    const nav = JSON.parse(raw);
    return (nav && (nav.tab || nav.projectId || nav.dealId)) ? nav : null;
  } catch {
    return null;
  }
}

/**
 * Boot-time glue: consume a marked reload and re-apply the user's spot. Re-open the
 * detail they had open — a project (requestProjectOpen → projects tab) or a deal
 * (requestDealOpen → pipeline tab, TICKET-219) — else just restore the tab. Returns
 * true if anything was restored. Deps are injected so the dispatch is unit-tested.
 */
export function applyReloadRestore({ setActiveTab, requestProjectOpen, requestDealOpen } = {}) {
  const nav = consumeReloadRestore();
  if (!nav) return false;
  if (nav.projectId && typeof requestProjectOpen === 'function') {
    requestProjectOpen(nav.projectId);
    setActiveTab?.('projects', 'event:update-reload-restore');
  } else if (nav.dealId && typeof requestDealOpen === 'function') {
    requestDealOpen(nav.dealId);
    setActiveTab?.('pipeline', 'event:update-reload-restore');
  } else if (nav.tab) {
    setActiveTab?.(nav.tab, 'event:update-reload-restore');
  }
  return true;
}

/**
 * Remember the active sub-tab of an open detail so a reload/remount restores it
 * instead of snapping back to Overview (TICKET-219). kind = 'deal' | 'project'.
 * Keyed per-entity so each detail reopens where you left it. No-op without an id.
 */
export function saveDetailTab(kind, id, tab) {
  const s = store();
  if (!s || !kind || !id || !tab) return;
  try { s.setItem(TAB_PREFIX + kind + ':' + id, tab); } catch { /* best-effort */ }
}

/** Read the remembered sub-tab for an open detail, or null. */
export function readDetailTab(kind, id) {
  const s = store();
  if (!s || !kind || !id) return null;
  try { return s.getItem(TAB_PREFIX + kind + ':' + id) || null; } catch { return null; }
}
